/**
 * resolveChestBones — name the chest cast for the ADR-021 measurement and
 * repair: the clavicle pair, the upper-arm pair (whose joints mark the
 * acromion — the clavicles' distal ends), the chest band's vertical span (top
 * spine joint → neck), the DELIBERATE weight donors (the clavicles' spine-chain
 * ancestors — ADR-021 §2, survey warning #3: freed mass flows to the spine, not
 * to whatever else sat in a vertex's palette), and the rig's declared world
 * triad (up/facing/lateral) so every downstream band/slice/droop computation
 * works on Z-up rigs as written.
 *
 * Resolution goes through the RigDescriptor MAPPING (canonical ontology IDs →
 * the rig's own bone names) — never name regexes: the comparative sweep
 * (ADR-021 companion probe) showed four naming vocabularies across families,
 * and the mapping is the one place family knowledge already lives (ADR-004).
 *
 * A rig that cannot resolve is NOT an error: ADR-021 §10 arms the repair for
 * the clavicle class only, and a prop, a quadruped, or a rig with unmapped
 * shoulders is out of the defect class. That is the typed
 * `{ kind: 'not-applicable' }` outcome, carrying what was missing — a skip a
 * surface can show, never a silent one (ADR-021 §7).
 *
 * Depends on: ir/rig-descriptor, ir/ontology, math/vec3. Depended on by:
 * weights/chest-integrity, weights/bound-influence, weights/support-field,
 * kernel index.
 */
import { SchemaValidationError } from '../ir/errors.js';
import type { Ontology } from '../ir/ontology.js';
import type { RigDescriptor } from '../ir/rig-descriptor.js';
import { vec3Cross, type Vec3Data } from '../math/vec3.js';

/** A cast member: the rig's own bone, its canonical role, and its bind seat. */
export interface ResolvedChestBone {
  readonly rigBone: string;
  readonly ontologyId: string;
  readonly worldPosition: Vec3Data;
}

/** The resolved chest cast + the rig's declared world triad. */
export interface ChestBones {
  readonly clavicleL: ResolvedChestBone;
  readonly clavicleR: ResolvedChestBone;
  readonly armL: ResolvedChestBone;
  readonly armR: ResolvedChestBone;
  /** Bottom of the chest band: the HIGHEST mapped spine joint (by up-coordinate). */
  readonly bandBottom: ResolvedChestBone;
  /** Top of the chest band: the neck joint. */
  readonly bandTop: ResolvedChestBone;
  /**
   * Deliberate donor bones for freed clavicle mass: the clavicles' spine-chain
   * ancestors (nearest first, at most two). Never empty on a resolved cast.
   */
  readonly donors: readonly ResolvedChestBone[];
  /** Every rig bone in the left clavicle's subtree (root included) — the arm chain. */
  readonly subtreeL: ReadonlySet<string>;
  readonly subtreeR: ReadonlySet<string>;
  /** Every rig bone in the left upper-arm's subtree (root included). */
  readonly armSubtreeL: ReadonlySet<string>;
  readonly armSubtreeR: ReadonlySet<string>;
  /** The rig's declared up axis as a world vector. */
  readonly up: Vec3Data;
  /** The rig's declared facing as a world vector. */
  readonly facing: Vec3Data;
  /** `up × facing` — the world lateral axis the band is capped along. */
  readonly lateral: Vec3Data;
}

/** Resolution outcome: the cast, or the named reason this rig is out of class. */
export type ChestBonesResolution =
  | { readonly kind: 'resolved'; readonly bones: ChestBones }
  | { readonly kind: 'not-applicable'; readonly missing: readonly string[] };

const AXIS: Record<string, Vec3Data> = {
  Y: { x: 0, y: 1, z: 0 },
  Z: { x: 0, y: 0, z: 1 },
  '+X': { x: 1, y: 0, z: 0 },
  '-X': { x: -1, y: 0, z: 0 },
  '+Z': { x: 0, y: 0, z: 1 },
  '-Z': { x: 0, y: 0, z: -1 },
};

/** The chest band's bottom candidates, poorest rig wins whichever it mapped. */
const BAND_BOTTOM_IDS = ['Spine2', 'Spine1', 'Spine'] as const;

/**
 * Band |lateral| cap as a fraction of the arm joint's lateral offset — the
 * probe's measured constant, shared by the gate (chest-integrity) and the
 * repair's arm-chain band restriction (bound-influence) so the two stages
 * mean the same region by "the chest band".
 */
export const CHEST_BAND_CAP_FRACTION = 0.82;

/**
 * Resolve the chest cast from a rig's mapping. See the module header for the
 * `not-applicable` contract; the returned `missing` names every canonical role
 * that could not be found, not just the first.
 */
export function resolveChestBones(rig: RigDescriptor, ontology: Ontology): ChestBonesResolution {
  // Drift guard: the canonical roles this module is written against must exist
  // in the LOADED ontology — a rename there is a wiring bug, not a rig gap.
  const gone = ['LeftShoulder', 'RightShoulder', 'LeftArm', 'RightArm', 'Neck', ...BAND_BOTTOM_IDS].filter(
    (id) => !ontology.byId.has(id),
  );
  if (gone.length > 0) {
    throw new SchemaValidationError(
      'Ontology',
      gone.map((id) => `resolveChestBones expects canonical id "${id}" — absent from ontology ${ontology.version}`),
    );
  }

  const byName = new Map(rig.bones.map((b) => [b.name, b]));
  const rigBoneFor = new Map<string, string>();
  for (const m of rig.mapping) {
    // First mapping entry wins on duplicates — mapping review owns conflicts.
    if (!rigBoneFor.has(m.ontologyId)) rigBoneFor.set(m.ontologyId, m.rigBone);
  }

  const missing: string[] = [];
  const resolve = (ontologyId: string): ResolvedChestBone | null => {
    const rigBone = rigBoneFor.get(ontologyId);
    const bone = rigBone !== undefined ? byName.get(rigBone) : undefined;
    if (bone === undefined) {
      missing.push(ontologyId);
      return null;
    }
    return { rigBone: bone.name, ontologyId, worldPosition: bone.worldBind.position };
  };

  const up = AXIS[rig.upAxis]!;
  const facing = AXIS[rig.facing]!;
  const lateral = vec3Cross(up, facing);

  const clavicleL = resolve('LeftShoulder');
  const clavicleR = resolve('RightShoulder');
  const armL = resolve('LeftArm');
  const armR = resolve('RightArm');
  const bandTop = resolve('Neck');

  // Band bottom: the highest mapped spine joint along the up axis, so the band
  // never dips into the belly on rigs that map the full spine chain.
  let bandBottom: ResolvedChestBone | null = null;
  for (const id of BAND_BOTTOM_IDS) {
    const rigBone = rigBoneFor.get(id);
    const bone = rigBone !== undefined ? byName.get(rigBone) : undefined;
    if (bone === undefined) continue;
    const candidate: ResolvedChestBone = { rigBone: bone.name, ontologyId: id, worldPosition: bone.worldBind.position };
    if (bandBottom === null || upCoord(candidate.worldPosition, up) > upCoord(bandBottom.worldPosition, up)) {
      bandBottom = candidate;
    }
  }
  if (bandBottom === null) missing.push(`one of ${BAND_BOTTOM_IDS.join('/')}`);

  // Deliberate donors: walk each clavicle's RIG parent chain, keep bones whose
  // mapped canonical role is a SPINE-CHAIN segment — nearest first, at most
  // two. Deliberately NOT "region === spine": that region also holds Hips (and
  // Neck), and freed chest mass must never flow to the pelvis.
  const DONOR_IDS = new Set(BAND_BOTTOM_IDS as readonly string[]);
  const donorNames: string[] = [];
  const ontologyIdFor = new Map<string, string>();
  for (const m of rig.mapping) {
    if (!ontologyIdFor.has(m.rigBone)) ontologyIdFor.set(m.rigBone, m.ontologyId);
  }
  for (const clav of [clavicleL, clavicleR]) {
    if (clav === null) continue;
    let cursor = byName.get(clav.rigBone)?.parent ?? null;
    let taken = 0;
    while (cursor !== null && taken < 2) {
      const ontologyId = ontologyIdFor.get(cursor);
      if (ontologyId !== undefined && DONOR_IDS.has(ontologyId)) {
        if (!donorNames.includes(cursor)) donorNames.push(cursor);
        taken += 1;
      }
      cursor = byName.get(cursor)?.parent ?? null;
    }
  }
  if (donorNames.length === 0) missing.push('a spine-chain donor (the clavicle parent chain maps none)');

  if (missing.length > 0) return { kind: 'not-applicable', missing };

  const donors: ResolvedChestBone[] = donorNames.map((name) => ({
    rigBone: name,
    ontologyId: ontologyIdFor.get(name)!,
    worldPosition: byName.get(name)!.worldBind.position,
  }));

  // Subtrees over the rig's own parent graph — arm-chain membership is subtree
  // membership, never a name regex (family-proof by construction, ADR-021 sweep).
  const children = new Map<string, string[]>();
  for (const b of rig.bones) {
    if (b.parent === null) continue;
    const list = children.get(b.parent);
    if (list === undefined) children.set(b.parent, [b.name]);
    else list.push(b.name);
  }
  const subtree = (root: string): ReadonlySet<string> => {
    const set = new Set<string>([root]);
    const queue = [root];
    while (queue.length > 0) {
      for (const c of children.get(queue.pop()!) ?? []) {
        if (!set.has(c)) {
          set.add(c);
          queue.push(c);
        }
      }
    }
    return set;
  };

  return {
    kind: 'resolved',
    bones: {
      clavicleL: clavicleL!,
      clavicleR: clavicleR!,
      armL: armL!,
      armR: armR!,
      bandBottom: bandBottom!,
      bandTop: bandTop!,
      donors,
      subtreeL: subtree(clavicleL!.rigBone),
      subtreeR: subtree(clavicleR!.rigBone),
      armSubtreeL: subtree(armL!.rigBone),
      armSubtreeR: subtree(armR!.rigBone),
      up,
      facing,
      lateral,
    },
  };
}

function upCoord(p: Vec3Data, up: Vec3Data): number {
  return p.x * up.x + p.y * up.y + p.z * up.z;
}
